/*
 * favorites.js
 * ------------------------------------------------------------
 * お気に入り（ウィッシュリスト）機能。
 * ログイン不要で、ブラウザの localStorage に商品ハンドルの配列を保存する。
 * 同じ端末・同じブラウザでのみ保持される（別端末・別ブラウザには同期されない）。
 *
 * 使い方:
 *  - window.SSJFavorites.isFavorite(handle)
 *  - window.SSJFavorites.toggle(handle)
 *  - window.SSJFavorites.remove(handle)
 *  - window.SSJFavorites.getAll()
 *
 *  - [data-favorite-toggle="{{ product.handle }}"] を持つボタンは自動的に
 *    お気に入りの追加/削除トグルボタンとして初期化される。
 *  - #favorites-app を持つ要素があれば、お気に入り一覧ページとして
 *    自動的に商品を読み込んで表示する。祖先に [data-favorites-wrapper] があり、
 *    #favorites-app に data-hide-when-empty="true" が指定されている場合は、
 *    お気に入りが0件の時にそのラッパーごと非表示にする（カートページ埋め込み用）。
 *
 *  - お気に入り追加時、Shopifyのカスタムイベント（analytics.publish）で
 *    "ssj_favorite_added" を発行する。Settings > Customer events で
 *    カスタムピクセルを追加し、このイベントを購読することで
 *    Meta（Facebook）のAddToWishlist等の外部トラッキングに連携できる。
 * ------------------------------------------------------------
 */
(function () {
  if (window.SSJFavorites) return;

  var STORAGE_KEY = 'ssj_favorites';

  function getFavorites() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function saveFavorites(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* localStorageが使えない環境（プライベートモード等）では何もしない */
    }
  }

  function isFavorite(handle) {
    if (!handle) return false;
    return getFavorites().indexOf(handle) !== -1;
  }

  function notifyChange(handle, favorited) {
    document.dispatchEvent(
      new CustomEvent('favorites:changed', {
        detail: { handle: handle, favorited: favorited },
      })
    );
  }

  /* ---------------- 外部トラッキング連携（Meta等） ---------------- */

  function publishFavoriteAddedEvent(handle) {
    if (
      !window.Shopify ||
      !window.Shopify.analytics ||
      typeof window.Shopify.analytics.publish !== 'function'
    ) {
      return;
    }

    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (product) {
        var variant =
          product && product.variants && product.variants[0] ? product.variants[0] : null;
        window.Shopify.analytics.publish('ssj_favorite_added', {
          handle: handle,
          productId: product ? product.id : null,
          variantId: variant ? variant.id : null,
          title: product ? product.title : handle,
          price: product ? product.price / 100 : null,
          currency: 'JPY',
        });
      });
  }

  function addFavorite(handle) {
    if (!handle) return;
    var list = getFavorites();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      saveFavorites(list);
      notifyChange(handle, true);
      publishFavoriteAddedEvent(handle);
    }
  }

  function removeFavorite(handle) {
    if (!handle) return;
    var list = getFavorites();
    var idx = list.indexOf(handle);
    if (idx !== -1) {
      list.splice(idx, 1);
      saveFavorites(list);
      notifyChange(handle, false);
    }
  }

  function toggleFavorite(handle) {
    if (!handle) return false;
    var nowFavorited = !isFavorite(handle);
    if (nowFavorited) {
      addFavorite(handle);
    } else {
      removeFavorite(handle);
    }
    return nowFavorited;
  }

  window.SSJFavorites = {
    getAll: getFavorites,
    isFavorite: isFavorite,
    toggle: toggleFavorite,
    add: addFavorite,
    remove: removeFavorite,
  };

  /* ---------------- トグルボタン（商品ページなど） ---------------- */

  function updateButtonState(btn) {
    var handle = btn.getAttribute('data-favorite-toggle');
    var favorited = isFavorite(handle);
    btn.classList.toggle('is-favorited', favorited);
    btn.setAttribute('aria-pressed', favorited ? 'true' : 'false');
    var labelEl = btn.querySelector('[data-favorite-label]');
    if (labelEl) {
      var addLabel = btn.getAttribute('data-label-add') || 'お気に入りに追加';
      var removeLabel = btn.getAttribute('data-label-remove') || 'お気に入り済み';
      labelEl.textContent = favorited ? removeLabel : addLabel;
    }
  }

  function initToggleButtons(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var buttons = scope.querySelectorAll('[data-favorite-toggle]');
    buttons.forEach(function (btn) {
      updateButtonState(btn);
      if (btn._ssjFavoritesInit) return;
      btn._ssjFavoritesInit = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var handle = btn.getAttribute('data-favorite-toggle');
        toggleFavorite(handle);
        updateButtonState(btn);
      });
    });
  }

  document.addEventListener('favorites:changed', function () {
    initToggleButtons(document);
  });

  function boot() {
    initToggleButtons(document);
    initFavoritesPage();
  }

  document.readyState !== 'loading' ? boot() : document.addEventListener('DOMContentLoaded', boot);

  /* テーマエディタでセクションが再読み込みされた時にも再初期化する */
  document.addEventListener('shopify:section:load', function (e) {
    initToggleButtons(e.target);
    if (e.target.querySelector('#favorites-app')) {
      initFavoritesPage();
    }
  });

  /* ---------------- お気に入り一覧ページ / カート埋め込み ---------------- */

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return (
        {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[c] || c
      );
    });
  }

  function formatMoney(cents, format) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, format);
      }
    } catch (e) {
      /* fall through */
    }
    var value = (cents / 100).toFixed(0);
    return '¥' + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function buildCard(product, moneyFormat) {
    var li = document.createElement('li');
    li.className = 'grid__item favorites-grid__item';
    li.setAttribute('data-favorite-item', product.handle);

    var image = product.featured_image || (product.images && product.images[0]) || null;
    var imageUrl = image ? image.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_533x.$1$2') : null;

    var price = formatMoney(product.price, moneyFormat);
    var compareAtPrice =
      product.compare_at_price && product.compare_at_price > product.price
        ? formatMoney(product.compare_at_price, moneyFormat)
        : null;

    li.innerHTML =
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
      '<div class="card card--standard card--media">' +
      '<div class="card__inner ratio color-scheme-2 gradient" style="--ratio-percent: 100%;">' +
      '<div class="card__media">' +
      '<div class="media media--transparent">' +
      (imageUrl
        ? '<img src="' +
          escapeHtml(imageUrl) +
          '" alt="' +
          escapeHtml(product.title) +
          '" loading="lazy" width="533" height="533">'
        : '') +
      '</div>' +
      '</div>' +
      '</div>' +
      '<div class="card__content">' +
      '<div class="card__information">' +
      '<h3 class="card__heading h5">' +
      '<a href="' +
      escapeHtml(product.url) +
      '" class="full-unstyled-link">' +
      escapeHtml(product.title) +
      '</a>' +
      '</h3>' +
      '<div class="card-information">' +
      '<div class="price">' +
      '<div class="price__container">' +
      (compareAtPrice
        ? '<span class="price-item price-item--sale price-item--last">' +
          price +
          '</span>' +
          '<span class="price-item price-item--regular"><s>' +
          compareAtPrice +
          '</s></span>'
        : '<span class="price-item price-item--regular">' + price + '</span>') +
      '</div>' +
      '</div>' +
      (product.available === false
        ? '<span class="badge badge--bottom-left color-scheme-3" style="display:inline-block;margin-top:0.5rem;">売り切れ</span>'
        : '') +
      '</div>' +
      '</div>' +
      '</div>' +
      '<button type="button" class="favorite-toggle-btn favorite-toggle-btn--card is-favorited" data-favorite-toggle="' +
      escapeHtml(product.handle) +
      '" aria-pressed="true" aria-label="お気に入りから削除">' +
      '<span class="favorite-toggle-btn__icon" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 21s-7.5-4.6-10-9.3C0.3 8.1 1.9 4.5 5.4 3.6c2-0.5 4 0.3 5.1 2 1-1.7 3.1-2.5 5.1-2 3.5 0.9 5.1 4.5 3.4 8.1C19.5 16.4 12 21 12 21z"/></svg>' +
      '</span>' +
      '</button>' +
      '</div>';

    return li;
  }

  function initFavoritesPage() {
    var root = document.getElementById('favorites-app');
    if (!root || root._ssjInit) return;
    root._ssjInit = true;

    var grid = root.querySelector('#favorites-grid');
    var emptyState = root.querySelector('#favorites-empty');
    var loadingState = root.querySelector('#favorites-loading');
    var moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';
    var hideWhenEmpty = root.getAttribute('data-hide-when-empty') === 'true';
    var sectionWrapper = root.closest('[data-favorites-wrapper]');

    function setSectionVisible(visible) {
      if (sectionWrapper) {
        sectionWrapper.hidden = !visible;
      }
    }

    function render() {
      var handles = getFavorites();

      if (loadingState) loadingState.hidden = true;

      if (handles.length === 0) {
        if (grid) {
          grid.hidden = true;
          grid.innerHTML = '';
        }
        if (hideWhenEmpty) {
          setSectionVisible(false);
        } else {
          setSectionVisible(true);
          if (emptyState) emptyState.hidden = false;
        }
        return;
      }

      setSectionVisible(true);
      if (emptyState) emptyState.hidden = true;

      Promise.all(
        handles.map(function (handle) {
          return fetch('/products/' + encodeURIComponent(handle) + '.js')
            .then(function (res) {
              if (!res.ok) return null;
              return res.json();
            })
            .catch(function () {
              return null;
            });
        })
      ).then(function (products) {
        if (!grid) return;
        grid.innerHTML = '';
        var validCount = 0;
        products.forEach(function (product) {
          if (!product) return;
          validCount++;
          grid.appendChild(buildCard(product, moneyFormat));
        });
        grid.hidden = validCount === 0;
        var hideNow = hideWhenEmpty && validCount === 0;
        setSectionVisible(!hideNow);
        if (emptyState) emptyState.hidden = hideNow ? true : validCount !== 0;
        initToggleButtons(grid);
      });
    }

    render();
    document.addEventListener('favorites:changed', render);
  }
})();
